import type { ContentBody, ContentBodyTypeEnum } from "@/types"

import type { ContentBodyAttributes } from "../_components/content-body-forms"
import { contentBodyAttributes } from "../tracks.constant"

/**
 * Seed a content body's form attributes from an existing (serialized) body,
 * starting from the type's defaults and overlaying only the fields the body
 * actually carries. Shared by the full track form (tracks/edit) and the
 * per-track edit slideout so their editing surfaces stay identical.
 *
 * `null`/`""` are treated as "empty" for form-bound string fields (an
 * intentional falsy fallback), so the inputs render controlled and blank
 * rather than showing a literal "null".
 */
export function mergeContentBodyAttributes(
  existingContentBody: ContentBody | null | undefined,
  contentType: ContentBodyTypeEnum,
): ContentBodyAttributes {
  const defaultAttributes = contentBodyAttributes[contentType] ?? {}
  const merged: ContentBodyAttributes = {
    ...(defaultAttributes as ContentBodyAttributes),
  }

  if (!existingContentBody) return merged

  if (existingContentBody.id !== undefined) merged.id = existingContentBody.id
  if (existingContentBody.duration_seconds !== undefined)
    merged.duration_seconds = existingContentBody.duration_seconds
  if (existingContentBody.reading_time !== undefined)
    merged.reading_time = existingContentBody.reading_time

  /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
  if (existingContentBody.caption !== undefined)
    merged.caption = existingContentBody.caption || ""
  if (existingContentBody.source !== undefined)
    merged.source = existingContentBody.source || ""
  if (existingContentBody.aspect !== undefined)
    merged.aspect = existingContentBody.aspect || ""
  if (existingContentBody.transcript !== undefined)
    merged.transcript = existingContentBody.transcript || ""
  if (existingContentBody.body !== undefined)
    merged.body = existingContentBody.body || ""
  if (existingContentBody.alt_text !== undefined)
    merged.alt_text = existingContentBody.alt_text || ""
  /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */

  if (existingContentBody.image_url !== undefined)
    merged.image_url = existingContentBody.image_url

  return merged
}
